import { Hero } from "@/components/home/Hero";
import { TopSellingProducts } from "@/components/home/TopSellingProducts";
import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { ProductGrid } from "@/components/products/ProductGrid";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { getCategoryNameById } from "@/hooks/use-categories";
import { useTopSellingProducts } from "@/hooks/use-top-selling-products";
import { supabase } from "@/integrations/supabase/client";
import { getSupplierNameById } from "@/lib/utils";
import { FormattedProduct } from "@/types/product";
import { isProduction } from "@/utils/environment";
import { ArrowRight, Clock, PackageIcon, PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const Index = () => {
	const [recentlyViewedProducts, setRecentlyViewedProducts] = useState<
		FormattedProduct[]
	>([]);
	const [recentlyAddedProducts, setRecentlyAddedProducts] = useState<
		FormattedProduct[]
	>([]);
	const [loading, setLoading] = useState(true);
	const { user, profile, isRetailer, isLoading: authLoading } = useAuth();
	const [scrollPosition, setScrollPosition] = useState(0);
	const { products: topSellingProducts, loading: loadingTopSelling } =
		useTopSellingProducts(4);

	const isProd = isProduction();

	useEffect(() => {
		if (user && isRetailer) {
			fetchRecentlyViewedProducts();
		}
	}, [user, isRetailer]);

	useEffect(() => {
		fetchRecentlyAddedProducts();
		const handleScroll = () => {
			const position = window.scrollY;
			setScrollPosition(position);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const fetchRecentlyViewedProducts = async () => {
		try {
			const { data: viewedProductsData, error: viewedError } =
				await supabase
					.from("retailer_product_views")
					.select("product_id, viewed_at")
					.eq("user_id", user?.id)
					.order("viewed_at", { ascending: false })
					.limit(4);

			if (viewedError) throw viewedError;

			if (viewedProductsData && viewedProductsData.length > 0) {
				const productIds = viewedProductsData.map(
					(item) => item.product_id
				);

				const { data: products, error: productsError } = await supabase
					.from("products")
					.select(
						`
						*, 
						product_images(*),
						supplier:supplier_id(
							id, 
							business_name,
							name,
							is_test
						)
					`
					)
					.in("id", productIds)
					.eq("deleted", false);

				if (productsError) throw productsError;

				const filteredProducts = isProd
					? products.filter((p) => !p.supplier?.is_test)
					: products.filter((p) => p.supplier?.is_test);

				const formattedProducts = await Promise.all(
					filteredProducts.map(async (product) => {
						const primaryImage = product.product_images.find(
							(img) => img.is_primary
						);
						const imageUrl =
							primaryImage?.image_url ||
							product.product_images[0]?.image_url ||
							"/placeholder.svg";

						const supplierName = product.supplier
							? product.supplier.business_name ||
							  product.supplier.name
							: await getSupplierNameById(product.supplier_id);

						const categoryName = await getCategoryNameById(
							product.category_id
						);

						return {
							id: product.id,
							name: product.name,
							brand: supplierName,
							description: product.description || "",
							price: product.price,
							image: imageUrl,
							category: categoryName,
							categoryId: product.category_id,
							tags: [],
							supplierId: product.supplier_id,
							msrp: product.msrp || undefined,
							caseQuantity: product.case_quantity || 1,
							totalViews: product.total_views,
							isTestSupplier: product.supplier?.is_test || false,
						};
					})
				);

				const sortedProducts = productIds
					.map((id) =>
						formattedProducts.find((product) => product.id === id)
					)
					.filter(Boolean) as FormattedProduct[];

				setRecentlyViewedProducts(sortedProducts);
				setLoading(false);
			}
		} catch (error) {
			console.error("Error fetching recently viewed products:", error);
		}
	};

	const fetchRecentlyAddedProducts = async () => {
		try {
			const { data: products, error } = await supabase
				.from("products")
				.select(
					`
					*,
					product_images(*),
					supplier:supplier_id(
						id, 
						business_name,
						name,
						is_test
					)
				`
				)
				.eq("deleted", false)
				.order("created_at", { ascending: false })
				.limit(8);

			if (error) throw error;

			const filteredProducts = isProd
				? products.filter((p) => !p.supplier?.is_test)
				: products.filter((p) => p.supplier?.is_test);

			const limitedProducts = filteredProducts.slice(0, 4);

			const formattedProducts = await Promise.all(
				limitedProducts.map(async (product) => {
					const primaryImage = product.product_images.find(
						(img) => img.is_primary
					);
					const imageUrl =
						primaryImage?.image_url ||
						product.product_images[0]?.image_url ||
						"/placeholder.svg";

					const supplierName = product.supplier
						? product.supplier.business_name ||
						  product.supplier.name
						: await getSupplierNameById(product.supplier_id);

					const categoryName = await getCategoryNameById(
						product.category_id
					);

					return {
						id: product.id,
						name: product.name,
						brand: supplierName,
						description: product.description || "",
						price: product.price,
						image: imageUrl,
						category: categoryName,
						categoryId: product.category_id,
						tags: [],
						supplierId: product.supplier_id,
						msrp: product.msrp || undefined,
						caseQuantity: product.case_quantity || 1,
						totalViews: product.total_views,
						isTestSupplier: product.supplier?.is_test || false,
					};
				})
			);

			setRecentlyAddedProducts(formattedProducts);
			setLoading(false);
		} catch (error) {
			console.error("Error fetching recently added products:", error);
		}
	};

	return (
		<div className="min-h-screen flex flex-col">
			<Navbar />
			<main className="flex-grow">
				{authLoading ? (
					<div className="flex items-center justify-center min-h-[50vh]">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black" />
					</div>
				) : (
					<>
						{user && isRetailer ? (
							<>
								<section className="relative overflow-hidden pt-20 md:pt-30">
									<div className="absolute inset-0 z-0">
										<div
											className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1601599963565-b7f49deb352a?q=80&w=3164&auto=format&fit=crop')] bg-cover bg-center bg-no-repeat"
											style={{
												opacity: 0.08,
												transform: `translateY(${
													scrollPosition * 0.1
												}px)`,
											}}
										/>
										<div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white" />
									</div>

									<div className="container relative z-10 max-w-6xl mx-auto px-10 md:px-10">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
											<div
												className="hidden md:block"
												style={{
													opacity: Math.max(
														1 -
															scrollPosition *
																0.005,
														0
													),
													transform: `translateY(-${
														scrollPosition * 0.3
													}px)`,
												}}>
												<img
													src="/images/welcome-hero.png"
													alt="Wholesale marketplace"
													className="rounded-2xl w-full h-auto object-cover shadow-2xl"
												/>
											</div>
											<div className="flex flex-col items-center md:items-start text-center md:text-left">
												<div
													className="transition-all duration-300 ease-out"
													style={{
														opacity: Math.max(
															1 -
																scrollPosition *
																	0.005,
															0
														),
														transform: `translateY(-${
															scrollPosition * 0.3
														}px)`,
													}}>
													<h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-semibold tracking-tight leading-tight mb-6 text-balance">
														Welcome, <br />
														{profile?.name.split(
															" "
														)[0] || "Retailer"}
													</h1>

													<p className="max-w-2xl md:max-w-none text-lg md:text-xl text-gray-600 mb-10">
														Discover new products
														and restock your
														favorites <br />
														from our trusted
														suppliers
													</p>

													<div className="flex flex-col sm:flex-row items-center md:items-start justify-center md:justify-start gap-5">
														<Link to="/products">
															<Button
																size="lg"
																variant="accent"
																className="gap-2 px-8 py-6 text-base w-full sm:w-auto">
																<PackageIcon className="h-4 w-4" />
																Browse All
																Products
															</Button>
														</Link>
														<Link to="/dashboard/orders">
															<Button
																variant="outline"
																size="lg"
																className="gap-2 px-8 py-6 text-base w-full sm:w-auto">
																View Your Orders
															</Button>
														</Link>
													</div>
												</div>
											</div>
										</div>
									</div>
								</section>

								{false && (
									<section className="py-12 bg-white">
										<div className="container max-w-6xl mx-auto px-6 md:px-10">
											<div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8">
												<div>
													<h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900 mb-2 flex items-center gap-2">
														<Clock className="h-6 w-6 text-accent" />
														Recently Viewed
													</h2>
													<p className="text-gray-600">
														Products you've been
														checking out
													</p>
												</div>
												<Link
													to="/products"
													className="mt-4 md:mt-0">
													<Button
														variant="outline"
														className="gap-2 border-gray-200 text-gray-700 hover:bg-gray-50">
														View all products
														<ArrowRight className="h-4 w-4" />
													</Button>
												</Link>
											</div>
											<ProductGrid
												products={
													recentlyViewedProducts
												}
											/>
										</div>
									</section>
								)}

								{false && (
									<section className="py-12 bg-gradient-to-b from-white to-gray-50">
										<TopSellingProducts
											products={topSellingProducts}
											loading={loadingTopSelling}
										/>
									</section>
								)}

								{false && (
									<section className="py-12 bg-gray-50">
										<div className="container max-w-6xl mx-auto px-6 md:px-10">
											<div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8">
												<div>
													<h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900 mb-2 flex items-center gap-2">
														<PlusIcon className="h-6 w-6 text-accent" />
														Recently Added
													</h2>
													<p className="text-gray-600">
														The newest products to
														our marketplace
													</p>
												</div>
												<Link
													to="/products"
													className="mt-4 md:mt-0">
													<Button
														variant="outline"
														className="gap-2 border-gray-200 text-gray-700 hover:bg-gray-50">
														View all products
														<ArrowRight className="h-4 w-4" />
													</Button>
												</Link>
											</div>
											{loading ? (
												<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
													{[...Array(4)].map(
														(_, i) => (
															<div
																key={i}
																className="bg-white rounded-lg overflow-hidden border animate-pulse">
																<div className="h-48 w-full bg-gray-200" />
																<div className="p-4 space-y-3">
																	<div className="h-4 bg-gray-200 rounded w-3/4" />
																	<div className="h-4 bg-gray-200 rounded w-1/2" />
																	<div className="h-4 bg-gray-200 rounded w-1/4" />
																</div>
															</div>
														)
													)}
												</div>
											) : (
												<ProductGrid
													products={
														recentlyAddedProducts
													}
												/>
											)}
										</div>
									</section>
								)}

								{!isProd && (
									<section className="py-6 bg-amber-50">
										<div className="container max-w-6xl mx-auto px-6 md:px-10 text-center">
											<div className="inline-flex items-center gap-2 text-amber-800 text-sm font-medium px-3 py-1 bg-amber-100 rounded">
												<span>Test Environment</span>
											</div>
											<p className="text-amber-700 mt-1">
												You are viewing test suppliers
												and products
											</p>
										</div>
									</section>
								)}
							</>
						) : (
							<>
								<Hero />

								{false && (
									<section className="py-12 bg-gray-50">
										<div className="container max-w-6xl mx-auto px-6 md:px-10">
											<div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8">
												<div>
													<h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900 mb-2 flex items-center gap-2">
														<PlusIcon className="h-6 w-6 text-accent" />
														Recently Added
													</h2>
													<p className="text-gray-600">
														The newest products to
														our marketplace
													</p>
												</div>
												<Link
													to="/products"
													className="mt-4 md:mt-0">
													<Button
														variant="outline"
														className="gap-2 border-gray-200 text-gray-700 hover:bg-gray-50">
														View all products
														<ArrowRight className="h-4 w-4" />
													</Button>
												</Link>
											</div>
											{loading ? (
												<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
													{[...Array(4)].map(
														(_, i) => (
															<div
																key={i}
																className="bg-white rounded-lg overflow-hidden border animate-pulse">
																<div className="h-48 w-full bg-gray-200" />
																<div className="p-4 space-y-3">
																	<div className="h-4 bg-gray-200 rounded w-3/4" />
																	<div className="h-4 bg-gray-200 rounded w-1/2" />
																	<div className="h-4 bg-gray-200 rounded w-1/4" />
																</div>
															</div>
														)
													)}
												</div>
											) : (
												<ProductGrid
													products={
														recentlyAddedProducts
													}
												/>
											)}
										</div>
									</section>
								)}

								{false && (
									<section className="py-8 bg-gradient-to-b from-white to-gray-50">
										<TopSellingProducts
											products={topSellingProducts}
											loading={loadingTopSelling}
										/>
									</section>
								)}

								<section className="py-20 bg-gray-50">
									<div className="container max-w-6xl mx-auto px-6 md:px-10">
										<div className="max-w-3xl mx-auto text-center">
											<h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-gray-900 mb-6">
												Ready to transform your F&B
												wholesale experience?
											</h2>
											{/* <p className="text-lg text-gray-600 mb-10">
												Join thousands of food &
												beverage brands and retailers
												already using Starch to
												streamline their wholesale
												operations.
											</p> */}
											<div className="flex flex-col sm:flex-row items-center justify-center gap-4">
												<Link to="/auth?signup=true">
													<Button
														size="lg"
														variant="accent"
														className="gap-2 px-8 py-6 text-base w-full sm:w-auto">
														Get Started
														<ArrowRight className="h-4 w-4" />
													</Button>
												</Link>
												<a
													href="mailto:help@starch.site"
													target="_blank"
													rel="noopener noreferrer">
													<Button
														variant="outline"
														size="lg"
														className="gap-2 px-8 py-6 text-base w-full sm:w-auto">
														Contact Us
													</Button>
												</a>
											</div>
										</div>
									</div>
								</section>

								{!isProd && (
									<section className="py-6 bg-amber-50">
										<div className="container max-w-6xl mx-auto px-6 md:px-10 text-center">
											<div className="inline-flex items-center gap-2 text-amber-800 text-sm font-medium px-3 py-1 bg-amber-100 rounded">
												<span>Test Environment</span>
											</div>
											<p className="text-amber-700 mt-1">
												You are viewing test suppliers
												and products
											</p>
										</div>
									</section>
								)}
							</>
						)}
					</>
				)}
			</main>
			<Footer />
		</div>
	);
};

export default Index;
